import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { CheckboxInputField } from "@/components/podkit/checkbox/CheckboxInputField";
import { Text } from "@/components/podkit/typography/Text";
import { WorkflowTrigger_PullRequestEvent } from "gitpod-next-api/gitpod/v1/workflow_pb";
import { useState, type FC } from "react";
import { ConfigureWebhookDialog } from "@/components/automations/ConfigureWebhookDialog";
import type { WorkflowEditFormTriggerData } from "@/components/automations/automation-edit-form-data";
import { WorkflowEditCodeContext } from "@/components/automations/AutomationEditCodeContext";
import { pullRequestEventName } from "@/utils/automations";
import type { PlainWorkflow } from "@/queries/automation-queries";

type WorkflowEditTriggerPullRequestProps = {
    workflow: PlainWorkflow;
    trigger: WorkflowEditFormTriggerData;
    disabled?: boolean;
    handleConfigChange: (config: WorkflowEditFormTriggerData) => void;
};

export const WorkflowEditTriggerPullRequest: FC<WorkflowEditTriggerPullRequestProps> = ({
    workflow,
    trigger,
    handleConfigChange,
    disabled = false,
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState<Set<WorkflowTrigger_PullRequestEvent>>(
        new Set(trigger.config?.pullRequest?.events),
    );

    const handleEventToggle = (event: WorkflowTrigger_PullRequestEvent, checked: boolean) => {
        const newSelectedEvents = new Set(selectedEvents);
        if (checked) {
            newSelectedEvents.add(event);
        } else {
            newSelectedEvents.delete(event);
        }

        handleConfigChange({
            ...trigger,
            config: {
                pullRequest: {
                    events: Array.from(newSelectedEvents),
                },
            },
        });
        setSelectedEvents(newSelectedEvents);
    };

    return (
        <Card variant="bordered" className="flex flex-col gap-4 rounded-lg border-border-base bg-surface-primary">
            <Text className="text-sm text-content-secondary">
                Ona listens for pull-request events from your Git provider.
            </Text>
            <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => setIsDialogOpen(true)}
                disabled={disabled}
                className="w-fit"
                data-testid="configure-webhook-button"
                data-tracking-id="configure-webhook-pull-request-trigger-config"
            >
                Configuration
            </Button>

            <div className="flex flex-col gap-4">
                <WorkflowEditCodeContext
                    context={trigger.context}
                    disabled={disabled}
                    onChange={(context) => handleConfigChange({ ...trigger, context: context })}
                />

                <div className="flex flex-col gap-2">
                    <Text className="text-base font-medium text-content-primary">Triggers on:</Text>
                    <div className="flex flex-row flex-wrap gap-4">
                        {[
                            WorkflowTrigger_PullRequestEvent.OPENED,
                            WorkflowTrigger_PullRequestEvent.UPDATED,
                            WorkflowTrigger_PullRequestEvent.APPROVED,
                            WorkflowTrigger_PullRequestEvent.MERGED,
                            WorkflowTrigger_PullRequestEvent.CLOSED,
                        ].map((event) => (
                            <CheckboxInputField
                                key={event}
                                label={pullRequestEventName(event)}
                                checked={selectedEvents.has(event)}
                                onChange={(checked) => handleEventToggle(event, checked)}
                                disabled={disabled}
                                data-testid={`pr-event-${pullRequestEventName(event).toLowerCase()}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <ConfigureWebhookDialog workflow={workflow} open={isDialogOpen} onClose={() => setIsDialogOpen(false)} />
        </Card>
    );
};
